// components/Sidebar.tsx - Mobile Responsive
import React, { useState, useEffect } from 'react';
import { 
  FaList, FaCheck, FaCircle, FaPhoneAlt, FaEdit, FaTrash, 
  FaMapPin, FaChevronLeft, FaChevronRight, FaLocationArrow,
  FaTimes, FaRoute, FaUser, FaCar, FaWalking, FaBicycle,
  FaInfoCircle,
  FaPlus
} from 'react-icons/fa';
import { Point, TravelProfile } from '../types';

interface SidebarProps {
  isOpen: boolean;
  points: Point[];
  route: number[] | null;
  visitedPoints: Set<number>;
  travelProfile: TravelProfile;
  totalDistance: number;
  duration: string;
  userLocation: [number, number] | null;
  onToggleSidebar: () => void;
  onToggleVisited: (id: number) => void;
  onEditPoint: (point: Point) => void;
  onRemovePoint: (id: number) => void;
  onCenterLocation: (lat: number, lng: number) => void;
  onTravelProfileChange: (profile: TravelProfile) => void;
  onAddDestination: () => void;
  onResetPoints: () => void;
  onRefreshMap: () => void;
  onToggleDirections: () => void;
  showDirections: boolean;
  isLoadingDirections: boolean;
  error: string | null;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  points,
  route,
  visitedPoints,
  travelProfile,
  totalDistance,
  duration,
  userLocation,
  onToggleSidebar,
  onToggleVisited,
  onEditPoint,
  onRemovePoint,
  onCenterLocation,
  onTravelProfileChange,
  onAddDestination,
  onResetPoints,
  onRefreshMap,
  onToggleDirections,
  showDirections,
  isLoadingDirections,
  error,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getVisitedCount = () => points.filter(p => p.visited).length;
  const getTotalCount = () => points.length;

  // Mobile overlay
  if (isMobile && isOpen) {
    return (
      <>
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[1500]"
          onClick={onToggleSidebar}
        />
        <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-2xl z-[1600] overflow-y-auto transition-transform duration-300">
          <SidebarContent
            points={points}
            route={route}
            visitedPoints={visitedPoints}
            travelProfile={travelProfile}
            totalDistance={totalDistance}
            duration={duration}
            userLocation={userLocation}
            onToggleVisited={onToggleVisited}
            onEditPoint={onEditPoint}
            onRemovePoint={onRemovePoint}
            onCenterLocation={onCenterLocation}
            onTravelProfileChange={onTravelProfileChange}
            onAddDestination={onAddDestination}
            onResetPoints={onResetPoints}
            onRefreshMap={onRefreshMap}
            onToggleDirections={onToggleDirections}
            showDirections={showDirections}
            isLoadingDirections={isLoadingDirections}
            error={error}
            onClose={onToggleSidebar}
            isMobile={true}
          />
        </div>
      </>
    );
  }

  // Desktop sidebar
  return (
    <>
      <button
        onClick={onToggleSidebar}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-[1500] bg-white shadow-lg rounded-r-lg p-2 hover:bg-gray-50 transition-all hidden md:flex"
        style={{ marginLeft: isOpen ? '340px' : '0' }}
      >
        {isOpen ? <FaChevronLeft /> : <FaChevronRight />}
      </button>

      <div className={`bg-white shadow-lg transition-all duration-300 overflow-hidden flex-shrink-0 z-[1000] ${isOpen ? 'w-80' : 'w-0'} hidden md:block`}>
        <div className="h-full overflow-y-auto">
          <SidebarContent
            points={points}
            route={route}
            visitedPoints={visitedPoints}
            travelProfile={travelProfile}
            totalDistance={totalDistance}
            duration={duration}
            userLocation={userLocation}
            onToggleVisited={onToggleVisited}
            onEditPoint={onEditPoint}
            onRemovePoint={onRemovePoint}
            onCenterLocation={onCenterLocation}
            onTravelProfileChange={onTravelProfileChange}
            onAddDestination={onAddDestination}
            onResetPoints={onResetPoints}
            onRefreshMap={onRefreshMap}
            onToggleDirections={onToggleDirections}
            showDirections={showDirections}
            isLoadingDirections={isLoadingDirections}
            error={error}
            onClose={onToggleSidebar}
            isMobile={false}
          />
        </div>
      </div>
    </>
  );
};

// Sidebar Content Component
interface SidebarContentProps {
  points: Point[];
  route: number[] | null;
  visitedPoints: Set<number>;
  travelProfile: TravelProfile;
  totalDistance: number;
  duration: string;
  userLocation: [number, number] | null;
  onToggleVisited: (id: number) => void;
  onEditPoint: (point: Point) => void;
  onRemovePoint: (id: number) => void;
  onCenterLocation: (lat: number, lng: number) => void;
  onTravelProfileChange: (profile: TravelProfile) => void;
  onAddDestination: () => void;
  onResetPoints: () => void;
  onRefreshMap: () => void;
  onToggleDirections: () => void;
  showDirections: boolean;
  isLoadingDirections: boolean;
  error: string | null;
  onClose: () => void;
  isMobile: boolean;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  points,
  route,
  visitedPoints,
  travelProfile,
  totalDistance,
  duration,
  userLocation,
  onToggleVisited,
  onEditPoint,
  onRemovePoint,
  onCenterLocation,
  onTravelProfileChange,
  onAddDestination,
  onResetPoints,
  onRefreshMap,
  onToggleDirections,
  showDirections,
  isLoadingDirections,
  error,
  onClose,
  isMobile,
}) => {
  const getVisitedCount = () => points.filter(p => p.visited).length;
  const getTotalCount = () => points.length;

  return (
    <div className="p-3">
      {/* Close button for mobile */}
      {isMobile && (
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FaList className="text-blue-500" />
            Destinations
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FaTimes className="text-gray-600" />
          </button>
        </div>
      )}

      {/* Quick Stats Bar */}
      <div className="grid grid-cols-4 gap-1.5 mb-3">
        <div className="bg-blue-50 rounded-lg p-1.5 text-center">
          <div className="text-xs font-bold text-blue-600">{points.length}</div>
          <div className="text-[10px] text-gray-500">Total</div>
        </div>
        <div className="bg-green-50 rounded-lg p-1.5 text-center">
          <div className="text-xs font-bold text-green-600">{getVisitedCount()}</div>
          <div className="text-[10px] text-gray-500">Done</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-1.5 text-center">
          <div className="text-xs font-bold text-orange-600">{points.length - getVisitedCount()}</div>
          <div className="text-[10px] text-gray-500">Left</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-1.5 text-center">
          <div className="text-xs font-bold text-purple-600">{totalDistance.toFixed(1)}km</div>
          <div className="text-[10px] text-gray-500">Distance</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div 
            className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${getTotalCount() > 0 ? (getVisitedCount() / getTotalCount()) * 100 : 0}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
          <span>Progress</span>
          <span>{getTotalCount() > 0 ? Math.round((getVisitedCount() / getTotalCount()) * 100) : 0}%</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-1.5 mb-3">
        <button
          onClick={onAddDestination}
          className="px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium flex items-center justify-center gap-1"
        >
          <FaPlus className="text-[10px]" /> Add
        </button>
        <button
          onClick={onToggleDirections}
          className={`px-2 py-1.5 rounded-lg transition-colors text-xs font-medium flex items-center justify-center gap-1 ${
            showDirections ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white hover:bg-purple-600'
          }`}
        >
          <FaInfoCircle className="text-[10px]" /> {showDirections ? 'Hide' : 'Show'} Route
        </button>
        <button
          onClick={onResetPoints}
          className="px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium flex items-center justify-center gap-1"
        >
          <FaRoute className="text-[10px]" /> Reset
        </button>
        <button
          onClick={onRefreshMap}
          className="px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium flex items-center justify-center gap-1"
        >
          <FaCircle className="text-[10px]" /> Refresh
        </button>
      </div>

      {/* Travel Mode */}
      <div className="flex gap-1 mb-3">
        <button
          onClick={() => onTravelProfileChange('driving-car')}
          className={`flex-1 px-2 py-1 rounded-lg transition-colors text-[10px] font-medium flex items-center justify-center gap-0.5 ${
            travelProfile === 'driving-car' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <FaCar className="text-[10px]" /> Drive
        </button>
        <button
          onClick={() => onTravelProfileChange('foot-walking')}
          className={`flex-1 px-2 py-1 rounded-lg transition-colors text-[10px] font-medium flex items-center justify-center gap-0.5 ${
            travelProfile === 'foot-walking' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <FaWalking className="text-[10px]" /> Walk
        </button>
        <button
          onClick={() => onTravelProfileChange('cycling-regular')}
          className={`flex-1 px-2 py-1 rounded-lg transition-colors text-[10px] font-medium flex items-center justify-center gap-0.5 ${
            travelProfile === 'cycling-regular' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <FaBicycle className="text-[10px]" /> Cycle
        </button>
      </div>

      {/* Location Info */}
      {userLocation && (
        <div className="bg-gray-50 rounded-lg p-1.5 mb-3">
          <div className="flex items-center gap-1">
            <FaUser className="text-red-500 text-[10px]" />
            <span className="text-[10px] text-gray-600">
              📍 {userLocation[0].toFixed(4)}, {userLocation[1].toFixed(4)}
            </span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-gray-500">
            <span>⏱️ {duration}</span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-2 py-1 rounded-lg text-[10px] mb-3">
          {error}
        </div>
      )}

      {isLoadingDirections && (
        <div className="text-[10px] text-blue-600 mb-3">
          <div className="animate-spin inline-block mr-1">⟳</div>
          Fetching directions...
        </div>
      )}

      {/* Points List */}
      <div className="space-y-1.5">
        {points.map((point, index) => {
          const isVisited = visitedPoints.has(point.id);
          const routeIndex = route ? route.indexOf(index + 1) : -1;
          const isStart = routeIndex === 1;
          const isEnd = routeIndex === route?.length - 1;
          
          return (
            <div 
              key={point.id}
              className={`p-2 rounded-lg border transition-all cursor-pointer hover:shadow-md ${
                isVisited 
                  ? 'bg-green-50 border-green-200' 
                  : isStart 
                  ? 'bg-green-50 border-green-300' 
                  : isEnd 
                  ? 'bg-purple-50 border-purple-300'
                  : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
              onClick={() => onCenterLocation(point.lat, point.lng)}
            >
              <div className="flex items-start gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleVisited(point.id);
                  }}
                  className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all mt-0.5 ${
                    isVisited 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : 'border-gray-300 hover:border-green-500'
                  }`}
                >
                  {isVisited ? <FaCheck className="text-[8px]" /> : <FaCircle className="text-[8px] text-gray-300" />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className={`text-xs font-medium truncate ${isVisited ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                      {point.name}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCenterLocation(point.lat, point.lng);
                      }}
                      className="text-blue-500 hover:text-blue-700 transition-colors flex-shrink-0"
                      title="Center on map"
                    >
                      <FaLocationArrow className="text-[8px]" />
                    </button>
                    {isStart && !isVisited && (
                      <span className="text-[8px] bg-green-200 text-green-700 px-1 py-0.5 rounded-full flex-shrink-0">
                        Start
                      </span>
                    )}
                    {isEnd && !isVisited && (
                      <span className="text-[8px] bg-purple-200 text-purple-700 px-1 py-0.5 rounded-full flex-shrink-0">
                        End
                      </span>
                    )}
                    {routeIndex > 0 && routeIndex < (route?.length || 0) - 1 && !isVisited && (
                      <span className="text-[8px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded-full flex-shrink-0">
                        #{routeIndex}
                      </span>
                    )}
                  </div>
                  
                  {point.contact && (
                    <p className={`text-[10px] flex items-center gap-0.5 ${isVisited ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaPhoneAlt className="text-blue-400 text-[8px]" />
                      {point.contact}
                    </p>
                  )}

                  {!isVisited && (
                    <div className="flex gap-1.5 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditPoint(point);
                        }}
                        className="text-[10px] text-blue-500 hover:text-blue-700 flex items-center gap-0.5"
                      >
                        <FaEdit className="text-[8px]" /> Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePoint(point.id);
                        }}
                        className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5"
                      >
                        <FaTrash className="text-[8px]" /> Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {points.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            <FaMapPin className="text-3xl mx-auto mb-1 opacity-50" />
            <p className="text-xs">No destinations yet</p>
            <p className="text-[10px]">Click "Add" to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};